// The browser side of ADR 0016: the CSP nonce for code that inserts <style> tags at run time, and
// uncaught errors reported to the api (scrubbed and rate limited there; at most a few per page load).

export { cspNonce, shareNonce } from './nonce.ts';

const MAX_REPORTS = 5;

export interface ClientErrorReport {
  kind: 'error' | 'unhandledrejection';
  message: string;
  path: string;
  source?: string;
  line?: number;
  column?: number;
  stack?: string;
}

/** The report of one uncaught error; `null` for noise that is not the app's (a browser extension). */
export function reportOf(
  kind: ClientErrorReport['kind'],
  err: unknown,
  where: { path: string; source?: string; line?: number; column?: number },
): ClientErrorReport | null {
  const e = err instanceof Error ? err : null;
  const message = (e ? `${e.name}: ${e.message}` : typeof err === 'string' ? err : 'non-error thrown').slice(
    0,
    2000,
  );
  if (where.source && !/^https?:\/\/[^/]*\//.test(where.source)) return null;
  return {
    kind,
    message,
    path: where.path.slice(0, 500),
    ...(where.source ? { source: where.source.split(/[?#]/, 1)[0]!.slice(0, 500) } : {}),
    ...(where.line !== undefined ? { line: where.line } : {}),
    ...(where.column !== undefined ? { column: where.column } : {}),
    ...(e?.stack ? { stack: e.stack.slice(0, 4000) } : {}),
  };
}

/** Posts uncaught errors and unhandled rejections to /api/telemetry/client-errors (no credentials). */
export function reportUncaughtErrors(win: Window & typeof globalThis = window) {
  let sent = 0;
  const send = (r: ClientErrorReport | null) => {
    if (!r || sent >= MAX_REPORTS) return;
    sent++;
    void win
      .fetch('/api/telemetry/client-errors', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(r),
        credentials: 'omit',
        keepalive: true,
      })
      .catch(() => {});
  };
  win.addEventListener('error', (ev) =>
    send(
      reportOf('error', ev.error ?? ev.message, {
        path: win.location.pathname,
        ...(ev.filename ? { source: ev.filename } : {}),
        ...(ev.lineno ? { line: ev.lineno } : {}),
        ...(ev.colno ? { column: ev.colno } : {}),
      }),
    ),
  );
  win.addEventListener('unhandledrejection', (ev) =>
    send(reportOf('unhandledrejection', ev.reason, { path: win.location.pathname })),
  );
}
